import {
  WizardHeader,
  WizardLink,
} from '@monorepo/shared/componentsV2/fullPageWizard/WizardHeader';
import { useLinkOnSameOrg } from '@monorepo/shared/hooks/navigation/useLinkOnSame/useLinkOnSameOrg';
import { useIsSuperAdmin } from '@monorepo/shared/hooks/permissions/useIsSuperAdmin';
import { NonEmptyArray } from 'mapistry-shared';
import React, { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { RouteEditMode, WizardStep, WizardStepUrlPart } from '../types';

interface EditWorkflowHeaderProps {
  canSubmit?: boolean;
  isSubmitting?: boolean;
  onSubmit?: () => unknown;
  wizardStep: WizardStep;
}

export function EditWorkflowHeader({
  canSubmit,
  isSubmitting = false,
  onSubmit,
  wizardStep,
}: EditWorkflowHeaderProps) {
  const isSuperAdmin = useIsSuperAdmin();
  const { mode = RouteEditMode.NEW, workflowId } = useParams();
  const isEditMode = mode === RouteEditMode.EDIT;
  const workflowExists = !!workflowId;

  const navigate = useNavigate();
  const baseLink = useLinkOnSameOrg('/data-setup/workflows');

  const handleClose = useCallback(() => {
    navigate(baseLink);
  }, [navigate, baseLink]);

  const breadcrumbsLinks = useMemo<NonEmptyArray<WizardLink>>(() => {
    const getNavLinkTo = (targetStep: WizardStep) =>
      `${baseLink}${workflowExists ? `/${workflowId}` : ''}/${
        isEditMode ? 'edit' : 'new'
      }/${WizardStepUrlPart[targetStep]}`;
    const breadcrumbs: NonEmptyArray<WizardLink> = [
      {
        completed: workflowExists,
        label: WizardStep.DETAILS,
        to: getNavLinkTo(WizardStep.DETAILS),
      },
      {
        disabled: !workflowExists,
        label: WizardStep.STEPS,
        to: getNavLinkTo(WizardStep.STEPS),
      },
      {
        disabled: !workflowExists,
        label: WizardStep.PREVIEW,
        to: getNavLinkTo(WizardStep.PREVIEW),
      },
    ];
    if (isEditMode && isSuperAdmin) {
      breadcrumbs.push({
        disabled: false,
        label: WizardStep.ANALYTICS_SYNC,
        to: getNavLinkTo(WizardStep.ANALYTICS_SYNC),
      });
    }
    return breadcrumbs;
  }, [workflowExists, baseLink, workflowId, isEditMode, isSuperAdmin]);

  return (
    <WizardHeader
      canSubmit={canSubmit}
      isEditMode={isEditMode}
      isSubmitting={isSubmitting}
      links={breadcrumbsLinks}
      onClose={handleClose}
      onSubmit={onSubmit}
      title={isEditMode ? 'Edit Workflow' : 'New Workflow'}
      wizardStep={wizardStep}
    />
  );
}
